/**
 * Class SearchAutocomplete, which is used to add auto complete feature to Magento Search by using handlebars templates
 */

define([
    'jquery',
    'debounce',
    'handlebars',
    'handlebarsHelpers',
    'markjs'
], (
    $,
    debounce,
    Handlebars
) => {
    class SearchAutocomplete {
        /**
         * Constructor
         *
         * @param {Object}  config Configuration options passed in through JSON
         *
         * @returns {null}
         */
        constructor(config) {
            const defaultConfig = {
                suggestionsDataURI: '/datasource/fetch/suggestions',
                searchContainerSelector: '[data-js=magento-search-block]',
                inputSelector: '[data-js=aligent-search-autocomplete-input]',
                clearButtonSelector: '[data-js=aligent-search-autocomplete-clear]',
                searchIconSelector: '[data-js=aligent-search-autocomplete-icon]',
                suggestionsContainerSelector: '[data-js=aligent-search-autocomplete]',
                suggestionsResultsSelector: '[data-js=search-autocomplete-results]',
                keyWordsSelector: '[data-js=search-autocomplete-keywords]',
                keywordsListSelector: '[data-js=search-autocomplete-keywords-list]',
                productsSelector: '[data-js=search-autocomplete-products]',
                hamburgerIconSelector: '[data-js=hamburger]',

                keyWordsTemplateIDSelector: '#aligent-search-autocomplete-suggestions-keywords',
                productsTemplateIDSelector: '#aligent-search-autocomplete-suggestions-products',
                hamburgerActiveClass: 'is-active',

                inputActiveClass: 'aligent-search-autocomplete-input--active',
                showResultsClass: 'aligent-search-autocomplete--show-results',

                productSuggestionsDisplayNum: 4
            };

            // Merge default config options and provided config
            this.config = { ...defaultConfig, ...config };

            this.suggestionsData = null;

            this.$input = $(this.config.inputSelector);
            this.$searchContainerSelector = $(this.config.searchContainerSelector);
            this.$clearButton = $(this.config.clearButtonSelector);
            this.$searchIcon = $(this.config.searchIconSelector);
            this.$suggestionsContainer = $(this.config.suggestionsContainerSelector);
            this.$suggestionsResultsContainer = $(this.config.suggestionsResultsSelector);
            this.$keyWordsContainer = $(this.config.keyWordsSelector);
            this.$productsContainer = $(this.config.productsSelector);
            this.$hamburgerIconSelector = $(this.config.hamburgerIconSelector);
            this.$hamburgerActiveClass = $(this.config.hamburgerActiveClass);

            this.keyWordsTemplate = Handlebars.compile($(this.config.keyWordsTemplateIDSelector).html());
            this.productsTemplate = Handlebars.compile($(this.config.productsTemplateIDSelector).html());

            this.attachListeners();
        }

        /**
         * Attach event listeners to elements
         *
         * @returns {null}
         */
        attachListeners() {
            /**
             * Listen to `keyup` event of search input box, debounce 50ms to fire ajax request
             */
            $(document).on('keyup', this.config.inputSelector, debounce((e) => {
                this.getSearchSuggestions(e.target.value);
            }, 200));

            /**
             * Listen to `focus` event of search input box, active search input, display clear button, then show suggestions container
             */
            $(document).on('focus', this.config.inputSelector, (e) => {
                if (this.suggestionsData != null) {
                    this.showSearchResults();
                } else {
                    this.getSearchSuggestions(e.currentTarget.value);
                }
            });

            /**
             * Listen to `click` event of search clear button, clear value of search input
             */
            $(document).on('click', this.config.clearButtonSelector, () => {
                this.$input.val('');
                this.clearSearchResults();
            });
						
            /**
             * Listen to `click` event of hamburger icon
             */
            $(document).on('click', this.config.hamburgerIconSelector, () => {
				this.$input.val('');
                this.clearSearchResults();
            });

            /**
             * Listen to `click` event of document, if target is not search block, then hide suggestions container
             */
            $(document).on('click', (event) => {
                const $clickTarget = $(event.target);
                // check to see if the current target on click is search container or its children, if it is, return to continue click event
                if ($clickTarget.is(this.config.searchContainerSelector) || $clickTarget.closest(this.config.searchContainerSelector).length > 0) {
                    return;
                }

                this.clearSearchResults();
            });
        }

        /**
         * Send request to get search suggestions based on search term
         *
         * @param {string} searchTerm User input search keyword
         *
         * @returns {null}
         */
        getSearchSuggestions(searchTerm) {
            if ($.trim(searchTerm) === '') {
                return this.clearSearchResults();
            }

            $.ajax({
                url: this.config.suggestionsDataURI,
                type: 'POST',
                data: { search: searchTerm },
                dataType: 'json'
            }).done((data) => {
                this.suggestionsData = data;
                this.updateSuggestionsContainer(searchTerm, data);
                this.showSearchResults();
            });
        }

        /**
         * Update search suggestions container with search term and suggestions data
         *
         * @param {string} searchTerm User input search keyword
         * @param {object} suggestionsData Object used for storing all the suggestions search results
         *
         * @returns {null}
         */
        updateSuggestionsContainer(searchTerm, suggestionsData) {
            if (this.$keyWordsContainer.length) {
                this.$keyWordsContainer.html(this.keyWordsTemplate(suggestionsData));

                const $keywordsListContainer = $(this.config.keywordsListSelector);
                if ($keywordsListContainer.length) {
                    $keywordsListContainer.unmark({
                        done: () => {
                            $keywordsListContainer.mark(searchTerm);
                        }
                    });
                }
            }

            if (this.$productsContainer.length) {
                const products = suggestionsData.products;
                if (products && products.length > this.config.productSuggestionsDisplayNum) {
                   suggestionsData.products = products.slice(0, this.config.productSuggestionsDisplayNum);
                }

                this.formatPrices(suggestionsData.products);
                this.$productsContainer.html(this.productsTemplate(suggestionsData));
            }

            if (this.$suggestionsContainer.length && this.$suggestionsResultsContainer) {
                this.$suggestionsContainer.append(this.$suggestionsResultsContainer);
            }
        }

        /**
         * Format prices for product of products array
         *
         * @param products
         */
        formatPrices(products) {
            products.forEach((product) => {
                product.price = product.price && parseFloat(product.price).toFixed(2);
                product.special_price = product.special_price && parseFloat(product.special_price).toFixed(2);
                product.rewards_price = product.rewards_price && parseFloat(product.rewards_price).toFixed(2);
            });
        }

        /**
         * Show search results in suggestions container
         *
         * @param {null}
         *
         * @returns {null}
         */
        showSearchResults() {
            this.toggleInputActiveState(true);
            this.toggleSearchIconShowingClear(true);
            this.toggleClearButton(true);
            this.toggleSuggestionsContainerShowResults(true);
            this.toggleClassUserInput(true);
        }

        /**
         * Clear search results
         *
         * @param {null}
         *
         * @returns {null}
         */
        clearSearchResults() {
            this.suggestionsData = null;
            this.toggleInputActiveState(false);
            this.toggleSuggestionsContainerShowResults(false);
            this.toggleSearchIconShowingClear(false);
            this.toggleClearButton(false);
            this.toggleClassUserInput(false);
        }

        /**
         * Toggle active state to search input box so that we can ensure
         * when there is suggestions results, the size of input box won't reduce,
         * and before and after icon can be applied
         *
         * @param {boolean} isActive Flag indicating if the search input is currently focused
         *
         * @returns {null}
         */
        toggleInputActiveState(isActive) {
            this.$input.toggleClass(this.config.inputActiveClass, isActive);
        }

        /**
         * Toggle clear search button to display or not
         *
         * @param {boolean} display Flag indicating if clear button should be shown or not
         *
         * @returns {null}
         */
        toggleClearButton(display) {
            if (this.$clearButton.length) {
                this.$clearButton.toggle(display);
            }
        }

        /**
         * For some odd reason, the compiler doesn't respect !display when used in the toggleClearButton function,
         * so we created this new function which gets called with opposite values.
         * 
         * @param {boolean} display Flag indicating if icon should be shown or not
         */
        toggleSearchIconShowingClear(display) {
            if (this.$searchIcon.length) {
                this.$searchIcon.toggleClass('search-icon-button-showing-clear', display);
            }
        }

        /**
        * add class to the block search if the user type something in the search box so we can show hide search icon accordingly
        *
        * @returns {null}
        */
        toggleClassUserInput(isActive) {
            this.$searchContainerSelector.toggleClass('input-typed', isActive);
        }

        /**
         * Toggle suggestions container to show results or not
         *
         * @param {boolean} isShowResults Flag indicating if search results in suggestions container should be shown or not
         *
         * @returns {null}
         */
        toggleSuggestionsContainerShowResults(isShowResults) {
            if (this.$suggestionsContainer.length) {
                this.$suggestionsContainer.toggleClass(this.config.showResultsClass, isShowResults);
            }
        }
    }

    return (config) => {
        const searchAutocomplete = new SearchAutocomplete(config);
        return searchAutocomplete;
    };
});
